import React from "react";
import { Box } from "@mui/material";
import { Check } from "@mui/icons-material";
import { colors } from "../../theme/colors";
import { alpha } from "@mui/material/styles";
import { MOBILE_NAV_MQ } from "./lobbySettingsStyles";

const ROW_TRANSITION = `
    background .30s cubic-bezier(.22,1,.36,1),
    border-color .30s cubic-bezier(.22,1,.36,1),
    transform .30s cubic-bezier(.22,1,.36,1),
    box-shadow .30s cubic-bezier(.22,1,.36,1)
`;

const PREMIUM_SHADOW = `
    0 12px 24px rgba(0,0,0,.18),
    0 0 0 1px rgba(255,180,60,.03)
`;

const UNSELECTED_BG = `
    linear-gradient(
        180deg,
        rgba(17, 17, 19, 0.94) 0%,
        rgba(13, 13, 14, 0.96) 100%
    )
`;

const SELECTED_BG = `
    linear-gradient(
        180deg,
        ${alpha(colors.primary.main, 0.18)},
        ${alpha(colors.primary.main, 0.09)}
    )
`;

const MATERIAL_TOP_HIGHLIGHT = {
    content: '""',
    position: "absolute",
    left: 0,
    right: 0,
    top: 0,
    height: "1px",
    background: `
        linear-gradient(
            90deg,
            transparent,
            rgba(255,255,255,.08),
            transparent
        )
    `,
    pointerEvents: "none",
};

function materialAfter(isChecked) {
    if (isChecked) {
        return {
            content: '""',
            position: "absolute",
            inset: 0,
            pointerEvents: "none",
            background: `
                linear-gradient(
                    135deg,
                    rgba(255,180,60,.05),
                    transparent 45%
                )
            `,
            opacity: 0.55,
        };
    }

    return {
        content: '""',
        position: "absolute",
        left: 0,
        right: 0,
        bottom: 0,
        height: "1px",
        background: "rgba(0,0,0,.35)",
        pointerEvents: "none",
    };
}

export function lobbySelectableRowSx(
    checked,
    {
        interactive = true,
        dimmed = false,
    } = {},
) {
    const base = {
        display: "flex",
        alignItems: "center",
        height: { xs: "3.25rem", xl: "3.9rem" },
        borderRadius: "0.5rem",
        transition: ROW_TRANSITION,
        overflow: "hidden",
        position: "relative",
        willChange: "transform",
        opacity: dimmed ? 0.4 : 1,
        cursor: interactive ? "pointer" : "default",
        "&::before": MATERIAL_TOP_HIGHLIGHT,
        "&::after": materialAfter(checked),
        [MOBILE_NAV_MQ]: {
            height: "2.75rem",
            borderRadius: "0.45rem",
        },
    };

    const selected = {
        background: SELECTED_BG,
        border: `1px solid ${alpha(colors.primary.main, 0.45)}`,
    };

    const unselected = {
        background: UNSELECTED_BG,
        border: `1px solid ${alpha(colors.border.default, 0.08)}`,
    };

    const unselectedHoverStyles = {
        background: UNSELECTED_BG,
        border: `1px solid ${alpha(colors.primary.main, 0.7)}`,
        transform: "translateY(-1px)",
        boxShadow: PREMIUM_SHADOW,
    };

    const selectedHoverStyles = {
        background: SELECTED_BG,
        border: `1px solid ${alpha(colors.primary.main, 0.7)}`,
        transform: "translateY(-1px)",
        boxShadow: `${PREMIUM_SHADOW}, ${colors.glow.primary}`,
        "& .checkbox": {
            borderColor: alpha(colors.primary.main, 0.28),
        },
    };

    if (!interactive) {
        return {
            ...base,
            ...(checked ? selected : unselected),
        };
    }

    return {
        ...base,
        ...(checked ? selected : unselected),
        "&:hover": checked ? selectedHoverStyles : unselectedHoverStyles,
        [MOBILE_NAV_MQ]: {
            ...base[MOBILE_NAV_MQ],
            "&:hover": {
                transform: "none",
            },
        },
    };
}

export function SelectableRowCheckbox({ checked }) {
    return (
        <Box
            className="checkbox"
            sx={{
                width: "22px",
                height: "22px",
                borderRadius: "7px",
                position: "relative",
                overflow: "hidden",
                background: checked
                    ? `
                        linear-gradient(
                            180deg,
                            ${colors.accent.main},
                            ${colors.primary.hover}
                        )
                    `
                    : `
                        linear-gradient(
                            180deg,
                            rgba(30,30,34,.96) 0%,
                            rgba(21,21,24,.98) 100%
                        )
                    `,
                border: checked
                    ? "1px solid transparent"
                    : `1px solid ${alpha(colors.border.default, 0.08)}`,
                padding: "0.25rem",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: ROW_TRANSITION,
                flexShrink: 0,
                boxSizing: "border-box",
                marginRight: 0,
                marginLeft: "auto",
                [MOBILE_NAV_MQ]: {
                    width: "18px",
                    height: "18px",
                    borderRadius: "6px",
                    marginRight: 0,
                    padding: "0.15rem",
                },
                boxShadow: checked
                    ? `
                        0 4px 10px rgba(0,0,0,.35),
                        inset 0 1px 0 rgba(255,255,255,.18),
                        0 0 10px rgba(255,180,60,.10)
                    `
                    : undefined,
                "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    height: "1px",
                    background: "rgba(255,255,255,.08)",
                    pointerEvents: "none",
                },
            }}
        >
            {checked ? (
                <Check
                    sx={{
                        color: colors.text.primary,
                        fontSize: "0.82rem",
                        filter: "drop-shadow(0 1px 1px rgba(0,0,0,.25))",
                    }}
                />
            ) : null}
        </Box>
    );
}

export default function LobbySelectableRow({
    checked,
    onClick,
    interactive = true,
    dimmed = false,
    unselectedHoverGlow: _unselectedHoverGlow = "primary",
    leading,
    middle,
    showCheckbox = true,
    sx,
}) {
    return (
        <Box
            onClick={interactive && onClick ? onClick : undefined}
            sx={{
                ...lobbySelectableRowSx(checked, {
                    interactive,
                    dimmed,
                }),
                ...sx,
            }}
        >
            {leading}
            <Box sx={{ flex: 1, minWidth: 0 }}>{middle}</Box>
            {showCheckbox ? <SelectableRowCheckbox checked={checked} /> : null}
        </Box>
    );
}
